import { z } from 'zod';
import { zClientAppName } from '../enums';
import { assertTypes, type NoNewFields } from '../primitives';

/**
 * When the JS updates of one app on one native version (EAS Update publishes), or of one app's web
 * build (web deploys), are offered or forced. A device compares its JS version, the
 * `deployment.yml` run number baked into its bundle (0 for the bundle embedded in a store build),
 * with these.
 * - One row per app + native version, never moved to another native version: a min set on one
 *   native version never applies to the next.
 * - One row per app's web build, `nativeVersion` `null`: the web apps, and pos-native's and
 *   admin-native's web builds.
 * - CI sets `latestJsVersion` on each publish or web deploy; a super admin sets `minJsVersion`.
 */
export const appUpdateJsPolicySchema = z.object({
  app: zClientAppName,
  /**
   * The native version (the major part of the store version) these JS updates run on. `null`: the
   * app's web build, which has none.
   */
  nativeVersion: z.number().nullable(),
  /** The last published JS version. Below it, the update is offered. `null`: nothing published yet. */
  latestJsVersion: z.number().nullable().default(null),
  /**
   * Below it, the update is forced. Can be above `latestJsVersion` (or set while it is `null`)
   * while the deploy that publishes it is in flight.
   */
  minJsVersion: z.number().nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type AppUpdateJsPolicy = z.output<typeof appUpdateJsPolicySchema>;

export const appUpdateJsPolicyStrictSchema = appUpdateJsPolicySchema.safeExtend({
  nativeVersion: z.int().min(1).nullable(),
  latestJsVersion: z.int().min(1).nullable(),
  minJsVersion: z.int().min(1).nullable(),
});

export const appUpdateJsPolicyCvxSchema = appUpdateJsPolicySchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type AppUpdateJsPolicyCvx = z.output<typeof appUpdateJsPolicyCvxSchema>;

assertTypes<NoNewFields<AppUpdateJsPolicy, AppUpdateJsPolicyCvx>>();
